"use client";
import { useEffect, useRef } from "react";
import { useSession } from "@/lib/authClient";
import { identifyUser, takeSignupMethod, track } from "@/lib/analytics";
import { readConsent } from "@/lib/consent";

// Relie l'état de session (Better Auth) à la mesure d'audience : monté une seule fois
// au layout racine, comme `FavoritesSync`.
//
// Pourquoi un DÉTECTEUR plutôt qu'un simple appel après l'inscription : par email, le
// compte n'est actif qu'après le clic dans le mail de confirmation — souvent depuis un
// autre écran, parfois le lendemain ; et par Google, on quitte le site et on revient
// sans jamais repasser par le formulaire. Dans les deux cas, le seul instant où l'on
// sait vraiment qu'un compte est né, c'est quand une session s'ouvre sur un compte
// tout juste créé. D'où la lecture de `createdAt`.

// Au-delà, c'est une simple connexion. Large exprès : le mail de confirmation peut
// mettre une minute à arriver, et l'inscription Google fait un aller-retour.
const FRESH_ACCOUNT_MS = 5 * 60 * 1000;

const REPORTED_KEY = "atlanx:signup-reported";

/**
 * Garde-fou anti-doublon, à l'échelle de l'onglet : sans lui, un rafraîchissement de
 * la page dans les 5 minutes qui suivent l'inscription renverrait l'événement.
 * `sessionStorage` et non `localStorage` : rien ne doit survivre à la fermeture de
 * l'onglet chez un visiteur qui a refusé le dépôt de traceurs.
 */
function alreadyReported(userId: string): boolean {
  try {
    return sessionStorage.getItem(REPORTED_KEY) === userId;
  } catch {
    return false;
  }
}

function markReported(userId: string): void {
  try {
    sessionStorage.setItem(REPORTED_KEY, userId);
  } catch {
    /* stockage indisponible : au pire un doublon, jamais un manque */
  }
}

export function AuthTracking() {
  const { data } = useSession();
  const user = data?.user;
  // `data` change de référence à chaque rafraîchissement de session alors que le
  // compte, lui, est le même : on ne traite donc chaque identifiant qu'une fois.
  const handled = useRef<string | null>(null);

  useEffect(() => {
    if (!user || handled.current === user.id) return;
    handled.current = user.id;

    // Rattache l'historique anonyme au compte — ce que `person_profiles:
    // "identified_only"` attend pour créer une fiche personne.
    // ⚠️ Seulement si le visiteur a accepté : en comptage anonyme, l'identifier
    // reviendrait à créer la fiche qu'il vient précisément de refuser.
    if (readConsent() === "granted") identifyUser(user.id);

    const age = Date.now() - new Date(user.createdAt).getTime();
    if (age >= 0 && age < FRESH_ACCOUNT_MS && !alreadyReported(user.id)) {
      markReported(user.id);
      track.userSignedUp({ method: takeSignupMethod() });
    }
  }, [user]);

  return null;
}
